"use client";

import Image from "next/image";
import { Calendar, Clock } from "lucide-react";
import { Link } from "@/i18n/routing";
import { BlogPostSummary } from "@/lib/blog";
import { cn } from "@/lib/utils";
import { useTranslations, useLocale } from "next-intl";
import { formatReadingTime } from "@/lib/format-reading-time";

type BlogHeroProps = {
  post: BlogPostSummary;
  ctaLabel: string;
};

const formatDate = (dateString: string) => {
  try {
    // Normalize date string to ensure consistent parsing
    const normalizedDate = dateString.trim();
    const date = new Date(normalizedDate);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString;
    }
    
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(date);
  } catch {
    return dateString;
  }
};

export const BlogHero = ({ post, ctaLabel }: BlogHeroProps) => {
  const { slug, title, excerpt, coverImage, date, readingTime, category } = post;
  const t = useTranslations("blog");
  const locale = useLocale();
  const formattedReadingTime = formatReadingTime(readingTime, t);

  return (
    <section className="relative overflow-hidden rounded-3xl border border-gray-200 bg-gradient-to-br from-gray-50 to-white p-8 md:p-12">
      <div className="grid gap-10 md:grid-cols-[minmax(0,1fr)_minmax(0,0.75fr)] md:items-center">
        <div className="space-y-6">
          {category && (
            <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-100 px-4 py-2 text-xs uppercase tracking-widest text-gray-700">
              {category}
            </span>
          )}
          <h1 className={cn(
            "font-bebas text-5xl text-gray-900 md:text-6xl lg:text-7xl",
            locale === "ar" ? "leading-[1.8]" : "leading-tight"
          )}>
            {title}
          </h1>
          <p className="max-w-2xl text-base text-gray-600 md:text-lg font-poppins">
            {excerpt}
          </p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-gray-600">
            <span className="inline-flex items-center gap-2">
              <Calendar size={18} className="text-gray-500" />
              {formatDate(date)}
            </span>
            {formattedReadingTime && (
              <span className="inline-flex items-center gap-2">
                <Clock size={18} className="text-gray-500" />
                {formattedReadingTime}
              </span>
            )}
          </div>
          <Link
            href={`/blog/${slug}`}
            className="inline-flex w-fit items-center justify-center rounded-full bg-gradient-to-r from-[#8100FF] to-[#323DFB] px-6 py-3 text-sm font-semibold uppercase tracking-widest text-white transition hover:scale-[1.02] hover:from-[#323DFB] hover:to-[#8100FF]"
          >
            {ctaLabel}
          </Link>
        </div>
        <div
          className={cn(
            "relative aspect-[16/9] overflow-hidden rounded-3xl border border-gray-200",
            "bg-gray-100"
          )}
        >
          {coverImage ? (
            <Image
              src={coverImage}
              alt={title}
              fill
              className="object-contain"
              priority
              sizes="(max-width: 768px) 100vw, 50vw"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-lg text-gray-400">
              GRITIZER Blog
            </div>
          )}
        </div>
      </div>
      <div className="pointer-events-none absolute -left-32 top-1/2 h-[380px] w-[380px] -translate-y-1/2 rounded-full bg-[#8100FF]/10 blur-3xl" />
      <div className="pointer-events-none absolute -right-28 top-1/3 h-[320px] w-[320px] -translate-y-1/2 rounded-full bg-[#323DFB]/10 blur-3xl" />
    </section>
  );
};

